/* eslint-disable camelcase */
import {setCookie, removeCookie} from '@/lib/cookie'
import {cloneDeep} from 'lodash'
import {ModuleFc} from '@/project'
import {updateObject} from '@/store/utils'
import {AuthState, UpdatePayload, SignInKakaoPayload} from './types'
import Axios from 'axios'
import isInAppBrowser from '@/validate/is-in-app-browser'
import {isExpiredToken} from './utils'
import {ALL_TOKENS_EXPIRED, KAKAO_SERVICE_TERM_IDS} from './constants'

function getBaseUrl(uri: string) {
  const matches = uri.match(/^(http|https):\/\/([a-z]|-|:|[0-9]|\.)+\//)
  if(!matches || matches.length < 1) {
    throw new Error('no uri')
  }
  return matches[0].replace(/\/$/, '')
}

const CLIENT_ID = process.env.VUE_APP_KAKAO_REST_API_KEY
const HOST = getBaseUrl(location.href)
const KAKAO_SERVICE_TERMS = KAKAO_SERVICE_TERM_IDS.join(',')

export {AuthState}

const initState: AuthState = {
  accessToken: null,
  email: null,
  expiredAt: null,
  failbackMain: null,
  failbackSignIn: null,
  failbackSignUpBiz: null,
  failbackSignUpPhone: null,
  firstAuth: true,
  id: null,
  identifier: null,
  isAutoPay: false,
  isPhone: false,
  kakaoAuthMode: isInAppBrowser(window && window.navigator.userAgent) ? 'REST_API' : 'JS_SDK',
  kakaoRefreshToken: null,
  kakaoToken: null,
  name: null,
  phone: null,
  phoneCode: null,
  refreshExpiredAt: null,
  saveKakaoToken: true,
  unsolvedPayAmount: 0,
  unsolvedPayCount: 0,
  viewIntro: false,
  allTokensExpired: false,
  matchToken: '',
}

const module: ModuleFc<AuthState> = (ctx) => ({
  namespaced: true,
  state: cloneDeep(initState),
  actions: {
    /**
     * sign-in Kakao (REST api) replace page to sign-in kakao (OAuth)
     * @param _
     * @param payload
     */
    signInKakaoByRestApi(_, payload: SignInKakaoPayload) {
      location.href = Axios.getUri({
        url: `${process.env.VUE_APP_API_KAKAO_URL}/oauth/authorize`,
        params: {
          'client_id': CLIENT_ID,
          'redirect_uri': `${HOST}/oauth`,
          'response_type': 'code',
          'service_terms': KAKAO_SERVICE_TERMS,
          'state': payload,
        },
      })
    },
    /**
     * sign-in Kakao (JS_SDK) replace page to sign-in kakao (OAuth)
     * @param _
     * @param payload
     */
    signInKakaoByJsSdk(_, payload: SignInKakaoPayload) {
      ctx.$kakao.Auth.authorize({
        redirectUri: `${HOST}/oauth`,
        state: encodeURI(JSON.stringify(payload)),
        prompt: 'select_account', 
        serviceTerms: KAKAO_SERVICE_TERMS,
        throughTalk: true,
      })
    },
    /**
     * sign-in Kakao (JS_SDK)
     * @param state
     * @param dispatch
     * @param payload
     */
    signInKakao({state, dispatch}, payload: SignInKakaoPayload) {
      if(state.kakaoAuthMode === 'JS_SDK') {
        dispatch('signInKakaoByJsSdk', payload)
      } else {
        dispatch('signInKakaoByRestApi', payload)
      }
    },
    /**
     * Refresh Kakao Token
     */
    async refreshKakaoToken({commit, state}, payload: { kakaoRefreshToken?: string } = {}) {
      const {kakaoRefreshToken = state.kakaoRefreshToken} = payload
      const result = await ctx.$axios({
        url: '/kakao/refresh-token',
        method: 'post',
        data: {
          grantType: 'refresh_token',
          clientId: CLIENT_ID,
          refreshToken: kakaoRefreshToken,
        },
      })
      const {kakaoToken, expiredAt} = result.data
      commit('update', {kakaoToken, expiredAt})
    },
    /**
     * get kakao Token by a code (OAuth)
     * @param commit
     * @param payload
     */
    async getKakaoToken({commit}, payload: {code: string}) {
      const result = await ctx.$axios({
        method: 'post',
        url: '/kakao/token',
        data: {
          grantType: 'authorization_code',
          clientId: CLIENT_ID,
          redirectUri: `${HOST}/oauth`,
          code: payload.code,
        },
      })
      commit('update', result.data)
    },
    /**
     * ConnectKakao with kakaoToken
     * if kakaoToken and kakaoRefreshToken is expired throw error
     */
    async connectKakao({commit, state, dispatch, rootGetters}, payload: {
      kakaoToken?: string,
      localTime?: any,
    } = {}) {
      const {localTime, kakaoToken: payloadKakaoToken} = payload
      const sales_id = localStorage.getItem('sales_id')
      localStorage.removeItem('sales_id')
      const utm_source = localStorage.getItem('utm_source')
      const utm_medium = localStorage.getItem('utm_medium')
      const utm_campaign = localStorage.getItem('utm_campaign')
      const ref_code = localStorage.getItem('ref_code')
      const match_token = localStorage.getItem('match_token')
      if(match_token) {
        commit('setMatchToken', match_token)
      }
      // mack sure having kakaoToken
      if(!payloadKakaoToken && isExpiredToken(state.expiredAt, state.kakaoToken, localTime)) {
        if(isExpiredToken(state.refreshExpiredAt, state.kakaoRefreshToken, localTime)) {
          commit('update', {allTokensExpired: true})
          throw new Error(ALL_TOKENS_EXPIRED)
        }
        await dispatch('refreshKakaoToken')
      }

      const {kakaoToken = state.kakaoToken} = payload
      return ctx
        .$axios({
          url: '/connect/kakao',
          method: 'post',
          data: {
            kakaoToken,
            __noRetry: true,
            sales_id,
          },
          params: {
            utm_source,
            utm_medium,
            utm_campaign,
            ref_code,
            match_token,
          }
        })
        .then((resp) => {
          const {data} = resp
          setCookie({name: 'userId', value: String(data.id)})
          setCookie({name: 'bizIds', value: rootGetters['biz/bizIds']?.join(',')})
          commit('update', data)
          commit('biz/update', {bizs: data.bizs}, {root: true})
          return data
        })
    },
    /**
     * Get user info
     */
    async getUser({commit, state}, payload: { id?: string } = {}) {
      const result = await ctx.$axios({
        url: '/users/$id',
        method: 'get',
        params: {
          get id() {
            return payload.id || state.id
          },
        },
      })

      const {data} = result
      commit('update', data)
      commit('biz/update', {bizs: data.bizs}, {root: true})
    },
    /**
     * Update user info
     */
    async updateUser({commit}, payload = {}) {
      const result = await ctx.$axios({
        url: '/users/$id',
        method: 'patch',
        data: payload,
      })
      commit('update', result.data)
    },
    async deleteUser() {
      await ctx.$axios({
        url: '/users/delete-account',
        method: 'post',
      })
    },
    /**
     * Get Phone code to resister the Phone number
     */
    async requestPhoneCode(
      {state, commit},
      payload: { kakaoToken?: string, id?: string, phone?: string } = {},
    ) {
      const {
        kakaoToken = state.kakaoToken,
        id = state.id,
        phone = state.phone,
      } = payload
      const result = await ctx.$axios({
        url: '/auth/request-phone-code',
        method: 'post',
        data: {
          kakaoToken,
          id,
          phone,
        },
      })
      commit('update', result.data)
    },
    /**
     * Resister Phone Number by a Phone code
     */
    async resisterPhone({commit, state}, payload: any = {}) {
      const {
        phoneCode = state.phoneCode,
        phone = state.phone,
      } = payload
      const result = await ctx.$axios({
        url: '/auth/resister-phone/',
        method: 'patch',
        data: {
          phoneCode,
          phone,
        },
      })
      commit('update', result.data)
    },
    signOut({commit}) {
      commit('clear')
      commit('biz/clear', null, {root: true})
      localStorage.clear()
      sessionStorage.clear()
      removeCookie('userId')
      removeCookie('bizIds')
    },
  },
  getters: {
    /**
     * Authenticated or not
     */
    authenticated(state): boolean {
      return Boolean(state.accessToken)
    },
    /**
     * Having a phone number or not
     * @param state
     */
    hasPhone(state): boolean {
      return Boolean(state.isPhone)
    },
    /**
     * Sign-up/biz page path
     */
    failbackSignUpBiz(state) {
      return state.failbackSignUpBiz || '/sign-up/biz'
    },
    /**
     * Sign-up/phone page path
     */
    failbackSignUpPhone(state) {
      return state.failbackSignUpPhone || '/sign-up/phone'
    },
    /**
     * Sign-in page path
     */
    failbackSignIn(state): string {
      return state.failbackSignIn || '/sign-in'
    },
    /**
     * Main page path
     */
    failbackMain(state): string {
      return state.failbackMain || '/'
    },
  },
  mutations: {
    /**
     * Update state
     * @param state Store.state
     * @param payload
     */
    update(state, payload: UpdatePayload) {
      updateObject(state, payload, [
        'id', 'accessToken', 'identifier', 'name', 'isPhone', 'email',
        'phoneCode', 'kakaoToken', 'phone',
        'viewIntro', 'refreshExpiredAt', 'kakaoRefreshToken', 'expiredAt', 'firstAuth',
        'allTokensExpired',
      ])
    },
    /**
     * Set default state
     * @param state
     */
    clear(state) {
      updateObject(state, initState)
    },
    setMatchToken(state, payload: string) {
      state.matchToken = payload
    }
  },
})

export default module
